import { Injectable } from '@angular/core';
import { AuthService, CustomerProductPrice, Price, Product, ProductInputQuantityConfigService } from 'gung-standard';
import { environment } from '../../environments/environment';
import {
  CheckoutSettingsGroup,
  HlDisplayPortalFeaturesService,
  PriceSettingsGroup,
  ProductListInfoSettingsGroup
} from './hl-display-portal-features.service';
import { first, forkJoin, map } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class HlProductInputQuantityConfigService extends ProductInputQuantityConfigService {
  isAnyStepAmount: boolean = false;
  useLowLimitLowAntForMinimumOrderQty: boolean = false;
  templatePortalEnabled: boolean = false;
  isSales: boolean = false;
  q_hl_invmgtclass_thatEnable_q_hl_sales_qty_asMOQ: string[] = [];

  constructor(
    protected hlDisplayPortalFeaturesService: HlDisplayPortalFeaturesService,
    protected authService: AuthService
  ) {
    super();

    this.authService
      .getRoles()
      .pipe(first())
      .subscribe(roles => {
        this.isSales = roles.includes('SALES');
      });

    forkJoin({
      priceSettingsGroup: this.hlDisplayPortalFeaturesService.getPortalFeatureGroup('priceSettingsGroup').pipe(
        first(),
        map(priceSettings => priceSettings as PriceSettingsGroup)
      ),
      orderCheckoutSettingsGroup: this.hlDisplayPortalFeaturesService
        .getPortalFeatureGroup('orderCheckoutSettingsGroup')
        .pipe(
          first(),
          map(orderCheckoutSettings => orderCheckoutSettings as CheckoutSettingsGroup)
        ),
      productListInfoSettingsGroup: this.hlDisplayPortalFeaturesService
        .getPortalFeatureGroup('productListInfoSettingsGroup')
        .pipe(
          first(),
          map(productListInfoSettingsGroup => productListInfoSettingsGroup as ProductListInfoSettingsGroup)
        )
    }).subscribe(({ priceSettingsGroup, orderCheckoutSettingsGroup, productListInfoSettingsGroup }) => {
      this.useLowLimitLowAntForMinimumOrderQty = priceSettingsGroup.useLowLimitLowAntForMinimumOrderQty;
      this.isAnyStepAmount = orderCheckoutSettingsGroup.changeToAnyQtyAsUser;
      this.templatePortalEnabled = !!productListInfoSettingsGroup.templatePortal;
      this.q_hl_invmgtclass_thatEnable_q_hl_sales_qty_asMOQ =
        productListInfoSettingsGroup.q_hl_invmgtclass_thatEnable_q_hl_sales_qty_asMOQ || [];
    });
  }

  getMinimumOrderQuantity(productId: string, product?: Product, price?: CustomerProductPrice): number {
    let result: number;
    if (this.templatePortalEnabled && product?.extra?.templatelimitlowant) {
      result = Number(product?.extra?.templatelimitlowant) || 1;
    } else if (this.useLowLimitLowAntForMinimumOrderQty) {
      result = price.backendPriceLevel?.extra?.prl?.limitlowant || 1;
    } else if (!product) {
      return 1;
    } else if (this.q_hl_invmgtclass_thatEnable_q_hl_sales_qty_asMOQ.includes(product.extra?.ar?.q_hl_invmgtclass)) {
      result = product.extra.salesQuantity || 1;
    } else if (!this.isSales) {
      result = product.extra.stepAmount;
    } else {
      result = product.extra.minimumOrderQuantity || 1;
    }

    // Needed since the effective MOQ can differ from product.extra.minimumOrderQuantity when derived from
    // pricelist data — decorating the product ensures downstream consumers (e.g. quantity inputs) stay in sync.
    if (result !== product.extra.minimumOrderQuantity) {
      product.extra.minimumOrderQuantity = result;
    }

    return result;
  }

  getStepAmount(productId: string, product?: Product, price?: CustomerProductPrice): number {
    let result: number;
    if (this.templatePortalEnabled && product?.extra?.templatelimitlowant) {
      result = Number(product?.extra?.templatelimitlowant) || 1;
    } else if (this.useLowLimitLowAntForMinimumOrderQty) {
      result = price.backendPriceLevel?.extra?.prl?.limitlowant || 1;
    } else if (!!product?.extra?.stepAmount) {
      if (typeof product?.extra?.stepAmount === 'string') {
        result = Number(product.extra.stepAmount.trim());
      } else if (typeof product?.extra?.stepAmount === 'number') {
        result = product.extra.stepAmount;
      }
    }
    result ??= 1;

    // Needed since the effective step can differ from product.extra.stepAmount when derived from
    // pricelist data — decorating the product ensures downstream consumers (e.g. quantity inputs) stay in sync.
    if (result !== product?.extra?.stepAmount) {
      product.extra.stepAmount = result;
    }

    return result;
  }

  getAllowManualQtyInputOutsideStepSizes(
    productId: string,
    product?: Product,
    price?: CustomerProductPrice,
    isAnonymous?: boolean,
    isUser?: boolean,
    isSales?: boolean,
    isAdmin?: boolean,
    isActuator?: boolean
  ): boolean {
    if (this.isAnyStepAmount || this.isSales || isAdmin) {
      return true;
    }

    return false;
  }
}
